import { useState, useRef, useCallback, useEffect } from 'react';
import * as Tone from 'tone';

export const useToneAudio = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // True from Play until a count-in's last click, while the song has not started
  const [isCountingIn, setIsCountingIn] = useState(false);
  const countInTimerRef = useRef(null);
  const [volume, setVolume] = useState(0); // Global volume
  const [partVolumes, setPartVolumes] = useState({}); // Per-part volumes
  const [overrideVolumesEnabled, setOverrideVolumesEnabled] = useState(false);
  const [overrideVolumes, setOverrideVolumes] = useState({}); // Override volumes
  const [instrument, setInstrument] = useState('piano');
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [velocity, setVelocity] = useState(0.5); // Default to 50%
  
  const partSynthsRef = useRef({});
  const scheduledToneEventsRef = useRef([]);
  const playbackStartTimeRef = useRef(0);
  const mutedPartsRef = useRef(new Set());
  const noteEventsRef = useRef([]);
  const pendingDisposalsRef = useRef([]);
  
  // Loop-specific refs
  const loopEventsRef = useRef([]);
  const loopDurationRef = useRef(0);
  const isLoopModeRef = useRef(false);

  // Live tempo refs: the BPM note-event times were baked at during preparePlayback,
  // and the ratio of the current Transport BPM to it (1 = no live adjustment)
  const preparedBPMRef = useRef(120);
  const tempoRatioRef = useRef(1);

  // Metronome. Enabled flag is a ref so startPlayback sees the latest value
  // without being recreated; the meter is set by the player from the score
  // before play. The during-playback click runs on the Transport so it stays
  // locked to the notes and rescales with live tempo changes; the count-off
  // runs on the raw audio clock because it has to fire before the Transport
  // starts. Meter values are in quarter notes so they are tempo-independent.
  const metronomeEnabledRef = useRef(false);
  const [metronomeEnabled, setMetronomeEnabledState] = useState(false);
  const meterRef = useRef({ beats: 4, beatQuarters: 1, pickupQuarters: 0 });
  const clickSynthRef = useRef(null);
  const clickRepeatIdRef = useRef(null);
  
  const globalSynthConfigRef = useRef({
    typeName: "piano",
    oscillator: null,
    volume: 0
  });

  const updateGlobalSynthSettings = useCallback(() => {
    const selectedValue = instrument;
    globalSynthConfigRef.current.volume = parseFloat(volume);
    globalSynthConfigRef.current.typeName = selectedValue.startsWith("Synth_") ? "Synth" : selectedValue;
    globalSynthConfigRef.current.oscillator = selectedValue.startsWith("Synth_") ? selectedValue.split("_")[1] : null;
  }, [instrument, volume]);

  // Set volume for a specific part
  const setPartVolume = useCallback((partId, volumeValue) => {
    setPartVolumes(prev => ({ ...prev, [partId]: volumeValue }));
    
    // Update the synth immediately if it exists
    const synth = partSynthsRef.current[partId];
    if (synth && synth.volume && !synth._disposed) {
      try {
        synth.volume.value = volumeValue;
      } catch (e) {
      }
    }
  }, []);

  // Get volume for a specific part (with fallback to 100%)
  const getPartVolume = useCallback((partId) => {
    return partVolumes[partId] !== undefined ? partVolumes[partId] : 100; // Default to 100%
  }, [partVolumes]);

  // Get override volume for a specific part (with fallback to 100%)
  const getOverrideVolume = useCallback((partId) => {
    return overrideVolumes[partId] !== undefined ? overrideVolumes[partId] : 100; // Default to 100%
  }, [overrideVolumes]);

  // Set override volume for a specific part
  const setOverrideVolume = useCallback((partId, volumeValue) => {
    setOverrideVolumes(prev => ({ ...prev, [partId]: volumeValue }));
  }, []);

  // Get confidence-blended volume for a specific part
  const getConfidenceBlendedVolume = useCallback((partId, selectedPart, confidence) => {
    if (selectedPart === null) {
      return 100; // Default if no part is selected as "me"
    }

    // Equal-power crossfade: the percentages become linear amplitude gains
    // downstream, and voice parts are uncorrelated (their power sums). A linear
    // fade (100-c / c) makes total power sag ~3dB at the midpoint, which is the
    // "too soft in the middle" problem. Using sin/cos weights keeps summed power
    // constant across the blend so overall loudness stays even.
    const t = confidence / 100;              // 0..1
    const angle = t * (Math.PI / 2);         // 0..π/2

    // Ensure both partId and selectedPart are compared as strings
    if (String(partId) === String(selectedPart)) {
      // Part A (me): full at confidence 0, silent at confidence 100
      return 100 * Math.cos(angle);
    } else {
      // Part B (others): silent at confidence 0, full at confidence 100
      return 100 * Math.sin(angle);
    }
  }, []);

  // Apply confidence settings to override volumes
  const applyConfidenceToOverrides = useCallback((partIds, selectedPart, confidence) => {
    if (selectedPart === null) return;
    
    const newOverrides = {};
    partIds.forEach(partId => {
      const volume = getConfidenceBlendedVolume(partId, selectedPart, confidence);
      newOverrides[partId] = volume;
    });
    setOverrideVolumes(newOverrides);
  }, [getConfidenceBlendedVolume]);

  // Initialize override volumes with current part volumes when override is enabled
  const initializeOverrideVolumes = useCallback((partIds) => {
    const newOverrides = {};
    partIds.forEach(partId => {
      newOverrides[partId] = getPartVolume(partId);
    });
    setOverrideVolumes(newOverrides);
  }, [getPartVolume]);

  // Track disposal state to prevent creating synths during disposal
  const isDisposingRef = useRef(false);

  const clearScheduledEvents = useCallback(() => {
    // Clear Transport events
    scheduledToneEventsRef.current.forEach(id => Tone.Transport.clear(id));
    scheduledToneEventsRef.current = [];
    
    // Clean up loop handler if it exists
    if (scheduledToneEventsRef.current.loopHandler) {
      Tone.Transport.off('loop', scheduledToneEventsRef.current.loopHandler);
      scheduledToneEventsRef.current.loopHandler = null;
    }
    
    // Drop the metronome repeat too; the next startPlayback reschedules it
    clickRepeatIdRef.current = null;

    // Cancel all Transport events
    Tone.Transport.cancel(0);
  }, []);

  // Convert a baked event time (seconds at the prepared BPM) into the equivalent
  // Transport seconds under the current BPM
  const toCurrentTime = useCallback((bakedTime) => bakedTime / tempoRatioRef.current, []);

  // A Transport tick position expressed in the baked-time frame. Ticks are
  // tempo-independent, so this stays aligned with baked event times across live
  // BPM changes (Tone stores scheduled events as ticks internally)
  const ticksToBakedTime = useCallback((ticks) => {
    return (ticks / Tone.Transport.PPQ) * (60 / preparedBPMRef.current);
  }, []);

  const getTransportBakedTime = useCallback(() => ticksToBakedTime(Tone.Transport.ticks), [ticksToBakedTime]);

  // Register the BPM that note events were just baked at (called from preparePlayback)
  const setPreparedBPM = useCallback((effectiveBPM) => {
    preparedBPMRef.current = effectiveBPM;
    tempoRatioRef.current = 1;
  }, []);

  // Change tempo during playback without rescheduling: already-scheduled events are
  // stored as ticks, so changing the Transport BPM rescales them in real time
  const applyLiveTempo = useCallback((effectiveBPM) => {
    Tone.Transport.bpm.value = effectiveBPM;
    tempoRatioRef.current = effectiveBPM / preparedBPMRef.current;
  }, []);

  const disposeAllSynths = useCallback(() => {
    // console.log('🗑️ disposeAllSynths called - disposing all synths');
    
    // Clear any pending disposals first
    pendingDisposalsRef.current.forEach(timeout => clearTimeout(timeout));
    pendingDisposalsRef.current = [];
    
    // Clear scheduled events
    clearScheduledEvents();
    
    // RADICAL SOLUTION: Don't dispose at all, just disconnect and clear references
    Object.values(partSynthsRef.current).forEach((synth, index) => { 
      if (synth && !synth._disposed && synth.disposed !== true) {
        try {
          
          // Just disconnect and release - don't dispose
          if (synth.disconnect) {
            synth.disconnect();
          }
          
          if (synth.releaseAll) {
            synth.releaseAll();
          }
          
          // Set volume to silence to prevent any sound
          if (synth.volume) {
            synth.volume.value = -Infinity;
          }
          
        } catch (e) {
        }
      }
    });
    
    // Clear references but don't dispose - let garbage collection handle it
    partSynthsRef.current = {};
    
  }, [clearScheduledEvents]);

  const scheduleNoteEvents = useCallback(() => {
    // console.log('🎵 scheduleNoteEvents called - clearing and re-scheduling events');
    clearScheduledEvents();
    
    if (noteEventsRef.current.length === 0) {
      return;
    }
    
    // console.log(`📝 Scheduling ${noteEventsRef.current.length} note events`);
    
    // Find the end time of the song (last note + its duration)
    let songEndTime = 0;
    noteEventsRef.current.forEach(event => {
      const noteEndTime = event.time + (event.duration || 0.5);
      if (noteEndTime > songEndTime) {
        songEndTime = noteEndTime;
      }
    });
    
    noteEventsRef.current.forEach(event => {
      const id = Tone.Transport.scheduleOnce(toneTime => {
        const partIdStr = String(event.partId);
        const isMuted = mutedPartsRef.current.has(partIdStr) || mutedPartsRef.current.has(event.partId);
        
        if (!isMuted) {
          const synthForPart = partSynthsRef.current[event.partId];
          if(synthForPart && !synthForPart._disposed && synthForPart.disposed !== true) {
            try {
              // console.log(`🎹 Playing note: ${event.pitch} on part ${event.partId} at time ${toneTime}`);
              synthForPart.triggerAttackRelease(event.pitch, event.duration / tempoRatioRef.current, toneTime, event.velocity);
            } catch (e) {
              // console.log(`❌ Error triggering note: ${e.message}`);
            }
          } else if (synthForPart && (synthForPart._disposed || synthForPart.disposed)) {
            // console.log(`🚫 Synth for part ${event.partId} is disposed`);
          } else {
            // console.log(`❓ No synth found for part ${event.partId}`);
          }
        } else {
          // console.log(`🔇 Part ${event.partId} is muted`);
        }
      }, toCurrentTime(event.time));
      scheduledToneEventsRef.current.push(id);
    });

    // Schedule auto-stop after the song ends (only if not in loop mode)
    if (!isLoopModeRef.current && songEndTime > 0) {
      const autoStopId = Tone.Transport.scheduleOnce(() => {
        // Use Transport.stop() which will trigger the 'stop' event listener
        Tone.Transport.stop();
      }, toCurrentTime(songEndTime) + 0.5); // Add small buffer

      scheduledToneEventsRef.current.push(autoStopId);
    }

  }, [clearScheduledEvents, toCurrentTime]);

  // Schedule remaining events after a pause (only events that haven't been played yet)
  const scheduleRemainingEvents = useCallback((fromTime) => {
    clearScheduledEvents();
    
    if (noteEventsRef.current.length === 0) {
      return;
    }
    
    // Filter events that haven't been played yet (after the pause time)
    const remainingEvents = noteEventsRef.current.filter(event => event.time >= fromTime);
    // console.log(`📝 Scheduling ${remainingEvents.length} remaining events from time ${fromTime} (total events: ${noteEventsRef.current.length})`);
    
    if (remainingEvents.length === 0) {
      // Paused after the last note started: resuming just finishes the song,
      // rather than leaving the Transport running in silence with no stop
      if (!isLoopModeRef.current) {
        const autoStopId = Tone.Transport.scheduleOnce(() => {
          Tone.Transport.stop();
        }, toCurrentTime(fromTime) + 0.5);
        scheduledToneEventsRef.current.push(autoStopId);
      }
      return;
    }
    
    // Check the time range of remaining events
    const nextEventTime = Math.min(...remainingEvents.map(e => e.time));
    const lastEventTime = Math.max(...remainingEvents.map(e => e.time));
    // console.log(`⏰ Next event at: ${nextEventTime.toFixed(2)}, Last event at: ${lastEventTime.toFixed(2)}`);
    
    // Find the end time of remaining events
    let songEndTime = 0;
    remainingEvents.forEach(event => {
      const noteEndTime = event.time + (event.duration || 0.5);
      if (noteEndTime > songEndTime) {
        songEndTime = noteEndTime;
      }
    });
    
    // Schedule remaining events with their original timing
    remainingEvents.forEach(event => {
      const id = Tone.Transport.scheduleOnce(toneTime => {
        const partIdStr = String(event.partId);
        const isMuted = mutedPartsRef.current.has(partIdStr) || mutedPartsRef.current.has(event.partId);
        
        if (!isMuted) {
          const synthForPart = partSynthsRef.current[event.partId];
          if(synthForPart && !synthForPart._disposed && synthForPart.disposed !== true) {
            try {
              // console.log(`🎹 Playing note: ${event.pitch} on part ${event.partId} at time ${toneTime}`);
              synthForPart.triggerAttackRelease(event.pitch, event.duration / tempoRatioRef.current, toneTime, event.velocity);
            } catch (e) {
              // console.log(`❌ Error triggering note: ${e.message}`);
            }
          } else if (synthForPart && (synthForPart._disposed || synthForPart.disposed)) {
            // console.log(`🚫 Synth for part ${event.partId} is disposed`);
          } else {
            // console.log(`❓ No synth found for part ${event.partId}`);
          }
        } else {
          // console.log(`🔇 Part ${event.partId} is muted`);
        }
      }, toCurrentTime(event.time));
      scheduledToneEventsRef.current.push(id);
    });

    // Schedule auto-stop after the song ends (only if not in loop mode)
    if (!isLoopModeRef.current && songEndTime > 0) {
      const autoStopId = Tone.Transport.scheduleOnce(() => {
        Tone.Transport.stop();
      }, toCurrentTime(songEndTime) + 0.5);

      scheduledToneEventsRef.current.push(autoStopId);
    }

  }, [clearScheduledEvents, toCurrentTime]);

  // Schedule loop events with Transport looping
  const scheduleLoopEvents = useCallback((loopEvents, loopDuration, onLoopStart) => {
    clearScheduledEvents();
    
    if (loopEvents.length === 0) {
      return;
    }

    // Set Transport to loop mode
    Tone.Transport.loopStart = 0;
    Tone.Transport.loopEnd = toCurrentTime(loopDuration);
    Tone.Transport.loop = true;
    
    // Reset divisi tracking initially
    if (osmdRefForDivisi.current) {
      osmdRefForDivisi.current._lastDivisiAnnouncements = new Set();
    }
    
    // Reset divisi tracking on each loop restart
    const loopHandler = () => {
      if (osmdRefForDivisi.current) {
        osmdRefForDivisi.current._lastDivisiAnnouncements = new Set();
      }
    };
    
    // Listen for loop events
    Tone.Transport.on('loop', loopHandler);
    
    // Store the handler for cleanup
    if (!scheduledToneEventsRef.current.loopHandler) {
      scheduledToneEventsRef.current.loopHandler = loopHandler;
    }

    loopEvents.forEach(event => {
      const id = Tone.Transport.scheduleRepeat(toneTime => {
        const partIdStr = String(event.partId);
        const isMuted = mutedPartsRef.current.has(partIdStr) || mutedPartsRef.current.has(event.partId);
        
        if (!isMuted) {
          const synthForPart = partSynthsRef.current[event.partId];
          if(synthForPart && !synthForPart._disposed && synthForPart.disposed !== true) {
            try {
              synthForPart.triggerAttackRelease(event.pitch, event.duration / tempoRatioRef.current, toneTime, event.velocity);
            } catch (e) {
            }
          }
        }
      }, toCurrentTime(loopDuration), toCurrentTime(event.time));
      scheduledToneEventsRef.current.push(id);
    });
  }, [clearScheduledEvents, toCurrentTime]);

  // A section played once rather than looped (Grade my pitch grades just
  // that section): its events at their times, and the Transport stops where
  // the section ends
  const scheduleSectionOnce = useCallback((sectionEvents, sectionDuration) => {
    clearScheduledEvents();
    if (sectionEvents.length === 0) {
      return;
    }
    Tone.Transport.loop = false;

    sectionEvents.forEach(event => {
      const id = Tone.Transport.scheduleOnce(toneTime => {
        const isMuted = mutedPartsRef.current.has(String(event.partId)) || mutedPartsRef.current.has(event.partId);
        if (isMuted) return;
        const synthForPart = partSynthsRef.current[event.partId];
        if (synthForPart && !synthForPart._disposed && synthForPart.disposed !== true) {
          try {
            synthForPart.triggerAttackRelease(event.pitch, event.duration / tempoRatioRef.current, toneTime, event.velocity);
          } catch (e) {
          }
        }
      }, toCurrentTime(event.time));
      scheduledToneEventsRef.current.push(id);
    });

    const stopId = Tone.Transport.scheduleOnce((time) => {
      Tone.Transport.stop(time);
    }, toCurrentTime(sectionDuration));
    scheduledToneEventsRef.current.push(stopId);
  }, [clearScheduledEvents, toCurrentTime]);

  // Jump to a specific time position during playback
  const jumpToTime = useCallback(async (targetTime, forceWasPlaying = null) => {
    // console.log(`🦘 Jumping to time: ${targetTime}, forceWasPlaying: ${forceWasPlaying}`);
    
    // Use the forced playing state if provided, otherwise use current state
    const wasPlaying = forceWasPlaying !== null ? forceWasPlaying : isPlaying;
    
    // Stop current playback
    Tone.Transport.stop();
    
    // Clear scheduled events
    clearScheduledEvents();
    
    // Stop all currently playing notes
    Object.values(partSynthsRef.current).forEach((synth) => { 
      if(synth && !synth._disposed && synth.disposed !== true) {
        try {
          if (synth.releaseAll) {
            synth.releaseAll();
          }
        } catch (e) {
          // console.log(`❌ Error releasing synth: ${e.message}`);
        }
      }
    });
    
    // Set Transport position to target time BEFORE scheduling
    Tone.Transport.position = toCurrentTime(targetTime);
    
    // Schedule remaining events from the target time
    scheduleRemainingEvents(targetTime);
    
    // Start playback from the target time
    Tone.Transport.start();
    
    // Add timestamp for race condition debugging
    const jumpCompleteTime = Date.now();
    // console.log(`⏱️ Jump sequence complete at ${jumpCompleteTime}, Transport running: ${Tone.Transport.state}`);
    
    // Restore playing state if we were playing before (small delay to ensure state sync)
    if (wasPlaying) {
      // console.log(`🔄 Restoring playing state: wasPlaying=${wasPlaying} at ${Date.now()}`);
      setTimeout(() => {
        const restoreTime = Date.now();
        setIsPlaying(true);
        setIsPaused(false);
        // console.log(`✅ Playing state restored at ${restoreTime} (${restoreTime - jumpCompleteTime}ms later), Transport.seconds=${Tone.Transport.seconds}`);
        
        // Extra safety: Force another state update after a short delay to ensure highlighting restarts
        setTimeout(() => {
          if (Tone.Transport.state === 'started') {
            // console.log(`🛡️ Safety check: ensuring state is synced with Transport`);
            setIsPlaying(true);
            setIsPaused(false);
          }
        }, 50);
      }, 10);
    } else {
      // console.log(`❌ Not restoring playing state: wasPlaying=${wasPlaying}`);
    }
    
    // console.log(`✅ Jumped to time ${targetTime} successfully, Transport.seconds=${Tone.Transport.seconds}`);
  }, [clearScheduledEvents, scheduleRemainingEvents, toCurrentTime, isPlaying, setIsPlaying, setIsPaused]);

  // beats/beatType is the time signature; pickupQuarters is how much of the
  // first bar is missing when playback starts on a pickup (anacrusis), so the
  // click grid can be anchored to the first full downbeat instead of to the
  // first note. Compound meters (6/8, 9/8, 12/8) are felt in dotted quarters.
  const setMeter = useCallback(({ beats, beatType, pickupQuarters = 0 }) => {
    const validBeats = Number.isFinite(beats) && beats > 0 ? beats : 4;
    const validType = Number.isFinite(beatType) && beatType > 0 ? beatType : 4;
    const compound = validType === 8 && validBeats >= 6 && validBeats % 3 === 0;
    meterRef.current = {
      beats: compound ? validBeats / 3 : validBeats,
      beatQuarters: compound ? 1.5 : 4 / validType,
      pickupQuarters: Number.isFinite(pickupQuarters) && pickupQuarters > 0 ? pickupQuarters : 0
    };
  }, []);

  const getClickSynth = useCallback(() => {
    if (!clickSynthRef.current || clickSynthRef.current.disposed) {
      // Short, bright, pitched tick. Downbeat is played a fifth up.
      clickSynthRef.current = new Tone.MembraneSynth({
        pitchDecay: 0.008,
        octaves: 2,
        envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 },
        volume: -6
      }).toDestination();
    }
    return clickSynthRef.current;
  }, []);

  // Visual beat: anyone can subscribe and is called in sync with each click
  // (Tone.Draw fires the callback when the audio clock reaches the click).
  const beatListenersRef = useRef(new Set());
  const addBeatListener = useCallback((listener) => {
    beatListenersRef.current.add(listener);
    return () => beatListenersRef.current.delete(listener);
  }, []);

  const clickAt = useCallback((time, isDownbeat) => {
    getClickSynth().triggerAttackRelease(isDownbeat ? 'E5' : 'A4', 0.03, time);
    if (beatListenersRef.current.size > 0) {
      Tone.Draw.schedule(() => {
        beatListenersRef.current.forEach(listener => listener(isDownbeat));
      }, time);
    }
  }, [getClickSynth]);

  // Per-beat click during playback on the meter's beat unit, anchored to the
  // first full downbeat. Ticks ("...i" times) are tempo-independent, so the
  // grid stays exact if the BPM is nudged live. With a count-off the pickup
  // was already counted, so the repeat starts at the downbeat itself.
  const scheduleMetronomeClicks = useCallback((fromFirstDownbeat = false) => {
    if (clickRepeatIdRef.current !== null) {
      Tone.Transport.clear(clickRepeatIdRef.current);
    }
    const { beats, beatQuarters, pickupQuarters } = meterRef.current;
    const ppq = Tone.Transport.PPQ;
    const beatTicks = Math.max(1, Math.round(beatQuarters * ppq));
    const downbeatTicks = Math.round(pickupQuarters * ppq);
    const startTicks = fromFirstDownbeat ? downbeatTicks : downbeatTicks % beatTicks;
    clickRepeatIdRef.current = Tone.Transport.scheduleRepeat((time) => {
      const beatIndex = Math.round((Tone.Transport.ticks - downbeatTicks) / beatTicks);
      clickAt(time, ((beatIndex % beats) + beats) % beats === 0);
    }, `${beatTicks}i`, `${startTicks}i`);
  }, [clickAt]);

  const setMetronomeEnabled = useCallback((enabled) => {
    metronomeEnabledRef.current = !!enabled;
    setMetronomeEnabledState(!!enabled);
    // Take effect immediately if the Transport is already running: start the
    // click on the next beat, or silence it
    if (enabled) {
      if (Tone.Transport.state === 'started' && clickRepeatIdRef.current === null) {
        scheduleMetronomeClicks();
      }
    } else if (clickRepeatIdRef.current !== null) {
      Tone.Transport.clear(clickRepeatIdRef.current);
      clickRepeatIdRef.current = null;
    }
  }, [scheduleMetronomeClicks]);

  // One measure of clicks on the raw audio clock, returning the audio time the
  // Transport should start at so the first full downbeat lands right after the
  // count. When the song opens with a pickup the Transport starts that much
  // earlier, so the pickup is sung under the last count-off clicks.
  const scheduleCountOff = useCallback((honourPickup = true) => {
    const { beats, beatQuarters, pickupQuarters } = meterRef.current;
    const secondsPerQuarter = 60 / Tone.Transport.bpm.value;
    const secondsPerBeat = secondsPerQuarter * beatQuarters;
    // Small lead so the first click is not in the past by the time it's scheduled
    const start = Tone.now() + 0.05;
    for (let i = 0; i < beats; i++) {
      clickAt(start + i * secondsPerBeat, i === 0);
    }
    const pickupSeconds = honourPickup ? pickupQuarters * secondsPerQuarter : 0;
    return Math.max(Tone.now(), start + beats * secondsPerBeat - pickupSeconds);
  }, [clickAt]);

  // options.countOff=false plays without the one-bar count-in even if the
  // metronome is on (used when the playback is being captured to a file);
  // options.countOff=true counts in even with the metronome off (a graded take).
  // options.once=true plays a selected section once instead of looping it.
  // Resuming from a pause never counts in: the count-off is for a fresh start.
  const startPlayback = useCallback(async (options = {}) => {
    const wantCountOff = options.countOff === true || (metronomeEnabledRef.current && options.countOff !== false);
    const scheduleSection = () => {
      if (options.once === true) scheduleSectionOnce(loopEventsRef.current, loopDurationRef.current);
      else scheduleLoopEvents(loopEventsRef.current, loopDurationRef.current);
    };
    if (Tone.context.state !== 'running') {
      await Tone.start();
    }
    
    if (isPaused) {
      // Resume from exact pause position
      // console.log(`▶️ Resuming from pause at time: ${playbackStartTimeRef.current}`);
      // console.log(`🎹 Available synths before resume: ${Object.keys(partSynthsRef.current)}`);
      if (isLoopModeRef.current) {
        scheduleSection();
      } else {
        scheduleRemainingEvents(playbackStartTimeRef.current);
      }
      if (metronomeEnabledRef.current) scheduleMetronomeClicks(false);
      Tone.Transport.start(undefined, toCurrentTime(playbackStartTimeRef.current));
    } else {
      if (isLoopModeRef.current) {
        scheduleSection();
      } else {
        scheduleNoteEvents();
      }
      if (metronomeEnabledRef.current) scheduleMetronomeClicks(wantCountOff);
      if (wantCountOff) {
        const songStart = scheduleCountOff(true);
        Tone.Transport.start(songStart);
        setIsCountingIn(true);
        clearTimeout(countInTimerRef.current);
        countInTimerRef.current = setTimeout(() => setIsCountingIn(false), Math.max(0, (songStart - Tone.context.currentTime) * 1000));
      } else {
        Tone.Transport.start();
      }
    }
    
    setIsPlaying(true);
    setIsPaused(false);
  }, [isPaused, scheduleNoteEvents, scheduleRemainingEvents, scheduleLoopEvents, scheduleSectionOnce, toCurrentTime, scheduleMetronomeClicks, scheduleCountOff]);

  const pausePlayback = useCallback(() => {
    if (Tone.Transport.state === "started") {
      // Store position in the baked-time frame so it stays valid across live tempo changes
      playbackStartTimeRef.current = getTransportBakedTime();
      // console.log(`⏸️ Pausing at time: ${playbackStartTimeRef.current}`);
      // console.log(`🎹 Available synths: ${Object.keys(partSynthsRef.current)}`);
      Tone.Transport.pause();
      clearTimeout(countInTimerRef.current);
      setIsCountingIn(false);
      setIsPlaying(false);
      setIsPaused(true);
    }
  }, [getTransportBakedTime]);

  // While paused, move where Play resumes (baked-time frame, as pausePlayback stores it)
  const setPausePosition = useCallback((time) => {
    playbackStartTimeRef.current = Math.max(0, time);
  }, []);

  const stopPlayback = useCallback(() => {
    // CRITICAL: Stop transport FIRST to prevent new events from scheduling
    Tone.Transport.stop();
    Tone.Transport.cancel(0);
    Tone.Transport.loop = false;
    
    // Clear all scheduled events
    clearScheduledEvents();
    
    // PROPER STOP: Use releaseAll() to immediately trigger release on all voices
    Object.values(partSynthsRef.current).forEach((synth, index) => { 
      if(synth && !synth._disposed && synth.disposed !== true) {
        try {
          // CRITICAL: releaseAll() immediately triggers release on ALL active voices
          // This ensures scheduled release events happen immediately instead of later
          if (synth.releaseAll) {
            synth.releaseAll();
          }
        } catch (e) {
        }
      }
    });
    
    Tone.Transport.position = 0;
    clearTimeout(countInTimerRef.current);
    setIsCountingIn(false);
    setIsPlaying(false);
    setIsPaused(false);
    playbackStartTimeRef.current = 0;
    
  }, [clearScheduledEvents]);

  const togglePartMute = useCallback((partId, isMuted) => {
    if (isMuted) {
      mutedPartsRef.current.add(String(partId));
    } else {
      mutedPartsRef.current.delete(String(partId));
      mutedPartsRef.current.delete(Number(partId));
    }
  }, []);

  const setNoteEvents = useCallback((events) => {
    noteEventsRef.current = events;
  }, []);

  // Set up loop mode with transformed events
  // Store OSMD ref for divisi tracking reset
  const osmdRefForDivisi = useRef(null);
  
  const setOSMDRef = useCallback((osmdRef) => {
    osmdRefForDivisi.current = osmdRef;
  }, []);

  const setLoopMode = useCallback((loopEvents, loopDuration) => {
    loopEventsRef.current = loopEvents;
    loopDurationRef.current = loopDuration;
    isLoopModeRef.current = true;
  }, []);

  // Exit loop mode and return to normal playback
  const exitLoopMode = useCallback(() => {
    // Clean up loop handler
    if (scheduledToneEventsRef.current.loopHandler) {
      Tone.Transport.off('loop', scheduledToneEventsRef.current.loopHandler);
      scheduledToneEventsRef.current.loopHandler = null;
    }
    
    Tone.Transport.loop = false;
    isLoopModeRef.current = false;
    loopEventsRef.current = [];
    loopDurationRef.current = 0;
  }, []);

  useEffect(() => {
    updateGlobalSynthSettings();
  }, [updateGlobalSynthSettings]);

  useEffect(() => {
    const handleTransportStop = () => {
      // Tone emits 'stop' on its next clock tick, not synchronously. When a
      // stop is immediately followed by a start (jump, restart), that event
      // arrives after the new run is under way; honouring it would flip
      // isPlaying to false while audio plays.
      if (Tone.Transport.state === 'started') return;
      if (!isPaused) {
        setIsPlaying(false);
        playbackStartTimeRef.current = 0;
      }
    };

    Tone.Transport.on('stop', handleTransportStop);
    return () => {
      Tone.Transport.off('stop', handleTransportStop);
      // Only dispose synths on component unmount, not on isPaused changes
    };
  }, [isPaused]);
  
  // Separate effect for component unmount cleanup
  useEffect(() => {
    return () => {
      disposeAllSynths();
    };
  }, [disposeAllSynths]);

  // Cleanup pending disposals and the metronome synth on unmount
  useEffect(() => {
    return () => {
      pendingDisposalsRef.current.forEach(timeout => clearTimeout(timeout));
      if (clickSynthRef.current && !clickSynthRef.current.disposed) {
        clickSynthRef.current.dispose();
        clickSynthRef.current = null;
      }
    };
  }, []);

  return {
    // State
    isPlaying,
    setIsPlaying,
    isPaused,
    isCountingIn,
    setIsPaused,
    volume,
    setVolume,
    partVolumes,
    setPartVolume,
    getPartVolume,
    overrideVolumesEnabled,
    setOverrideVolumesEnabled,
    overrideVolumes,
    setOverrideVolume,
    getOverrideVolume,
    getConfidenceBlendedVolume,
    applyConfidenceToOverrides,
    initializeOverrideVolumes,
    instrument,
    setInstrument,
    playbackSpeed,
    setPlaybackSpeed,
    velocity,
    setVelocity,
    
    // Refs
    partSynthsRef,
    scheduledToneEventsRef,
    mutedPartsRef,
    globalSynthConfigRef,
    noteEventsRef,
    pendingDisposalsRef,
    loopEventsRef,
    loopDurationRef,
    isDisposingRef,
    
    // Actions
    startPlayback,
    pausePlayback,
    stopPlayback,
    togglePartMute,
    disposeAllSynths,
    clearScheduledEvents,
    updateGlobalSynthSettings,
    scheduleNoteEvents,
    scheduleRemainingEvents,
    jumpToTime,
    setPausePosition,
    setNoteEvents,
    scheduleLoopEvents,
    setLoopMode,
    exitLoopMode,
    setOSMDRef,
    setPreparedBPM,
    applyLiveTempo,
    getTransportBakedTime,
    ticksToBakedTime,
    metronomeEnabled,
    setMetronomeEnabled,
    setMeter,
    addBeatListener
  };
};